/**
 * LevelUpPopup.tsx — RPG 레벨업 축하 팝업
 *
 * 사용법:
 *   progressStore의 levelUpEvent가 null 이 아닐 때 렌더링
 *   닫기 시 clearLevelUpEvent() 호출
 */

import React, { useEffect, useState } from 'react'
import { useProgressStore, LEVEL_XP_TABLE } from '../store/progressStore'

export default function LevelUpPopup() {
  const { levelUpEvent, clearLevelUpEvent } = useProgressStore(s => ({
    levelUpEvent: s.levelUpEvent,
    clearLevelUpEvent: s.clearLevelUpEvent,
  }))

  const [visible, setVisible] = useState(false)
  const [animating, setAnimating] = useState(false)

  useEffect(() => {
    if (levelUpEvent) {
      setVisible(true)
      setAnimating(false)
      // 약간 딜레이 후 애니메이션 트리거
      const t = setTimeout(() => setAnimating(true), 30)
      return () => clearTimeout(t)
    } else {
      setVisible(false)
    }
  }, [levelUpEvent])

  const handleClose = () => {
    setAnimating(false)
    setTimeout(() => {
      setVisible(false)
      clearLevelUpEvent()
    }, 300)
  }

  // 5초 후 자동 닫기
  useEffect(() => {
    if (!visible) return
    const t = setTimeout(handleClose, 5000)
    return () => clearTimeout(t)
  }, [visible])

  if (!visible || !levelUpEvent) return null

  const { newLevel, title, emoji } = levelUpEvent
  const nextXp = LEVEL_XP_TABLE[newLevel + 1]

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      aria-live="polite"
    >
      {/* 반투명 오버레이 */}
      <div
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        style={{
          opacity: animating ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
        onClick={handleClose}
      />

      {/* 팝업 카드 */}
      <div
        className="relative pointer-events-auto w-80 max-w-[90vw]"
        style={{
          transform: animating ? 'scale(1) translateY(0)' : 'scale(0.7) translateY(40px)',
          opacity: animating ? 1 : 0,
          transition: 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease',
        }}
      >
        {/* 배경 파티클 링 */}
        <div className="absolute -inset-4 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.3) 0%, transparent 70%)',
          animation: animating ? 'ping 1s ease-out' : 'none',
        }} />

        <div
          className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)' }}
        >
          {/* 상단 배지 */}
          <div className="text-center pt-8 pb-4 px-6">
            <div className="relative inline-block">
              {/* 빛나는 링 */}
              <div className="absolute inset-0 rounded-full"
                style={{
                  background: 'rgba(251,191,36,0.2)',
                  transform: 'scale(1.6)',
                  animation: 'pulse 2s infinite',
                }} />
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center text-5xl shadow-lg mx-auto"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }}
              >
                {emoji}
              </div>
            </div>

            {/* LEVEL UP 텍스트 */}
            <div className="mt-4">
              <p className="text-yellow-400 text-xs font-bold tracking-[0.3em] uppercase mb-1">
                ✨ Level Up! ✨
              </p>
              <p className="text-white text-4xl font-black leading-none">
                Lv. {newLevel}
              </p>
              <p className="text-yellow-300 text-lg font-bold mt-1">
                {title}
              </p>
            </div>
          </div>

          {/* 축하 메시지 */}
          <div className="px-6 pb-4">
            <div className="bg-white/10 rounded-2xl p-4 text-center">
              <p className="text-white/80 text-sm leading-relaxed">
                {getLevelMessage(newLevel)}
              </p>
            </div>
          </div>

          {/* 다음 레벨 안내 */}
          {nextXp && (
            <div className="px-6 pb-4">
              <div className="flex items-center justify-between text-xs text-white/50 mb-1.5">
                <span>다음 레벨까지</span>
                <span>Lv. {newLevel + 1}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-yellow-400 to-amber-300"
                  style={{ width: '0%', transition: 'width 1s ease 0.5s' }}
                />
              </div>
            </div>
          )}

          {/* 닫기 버튼 */}
          <div className="px-6 pb-6">
            <button
              onClick={handleClose}
              className="w-full py-3 rounded-2xl font-black text-sm text-indigo-900 shadow-lg"
              style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
            >
              계속 학습하기 🚀
            </button>
          </div>

          {/* 자동 닫기 프로그레스 바 */}
          <div className="h-1 bg-white/10">
            <div
              className="h-full bg-yellow-400/60"
              style={{
                width: animating ? '0%' : '100%',
                transition: animating ? 'width 5s linear' : 'none',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function getLevelMessage(level: number): string {
  if (level <= 2) return '첫 발걸음을 내디뎠어요! 이 속도라면 금방 수포자 탈출할 수 있어요. 💪'
  if (level <= 4) return '꾸준히 성장하고 있어요! 개념들이 연결되기 시작하는 게 느껴지나요? 🌿'
  if (level <= 6) return '절반을 넘어섰어요! 기울기와 변화율, 이제 진짜 보이기 시작하죠? 📈'
  if (level <= 9) return '수포자 탈출이 눈앞이에요! 여기까지 온 당신, 정말 대단합니다. 🔥'
  if (level === 10) return '🎊 수포자 탈출 완료! 포기했던 수학을 다시 정복했어요. 진심으로 축하합니다! 🏆'
  if (level <= 12) return '수학 고수의 길을 걷고 있어요. 이제 남들에게 가르쳐 줄 수 있는 실력이에요! ⭐'
  return '당신은 이미 수학 마스터입니다. 다음 목표를 향해 달려가세요! 👑'
}
